import React from "react";
import styled from "styled-components";
import { colors, typography, spacing, media } from "../styles/designSystem";
import { PageHeadline, SectionHeadline, TerminalLabel } from "../components/Typography";
import InfoCard from "../components/sub-components/InfoCard";
import MetricsCard from "../components/sub-components/MetricsCard";
import NDADisclosure from "../components/NDADisclosure";
import {
    ContrastPair,
    DomainGrid,
    PipelineFlow,
    StepGrid,
} from "../components/diagrams/primitives";

/**
 * tilt by Smarterhome.
 *
 * The one hardware-adjacent role on the site before Nebo. The page is built around
 * the thing that made it different from ordinary mobile work: the app was never
 * the whole product. It was the control surface for devices that lived in
 * someone's house, so every screen had a physical object on the other side of it.
 */

const PageWrapper = styled.div`
    max-width: 1100px;
    margin: 0 auto;
    padding: ${spacing[900]} ${spacing[700]} ${spacing[1100]};
    color: ${({ theme }) => theme.text};

    ${media.md} {
        padding: ${spacing[700]} ${spacing[400]} ${spacing[900]};
    }
`;

const Header = styled.div`
    border: 1px solid ${({ theme }) => theme.borderMedium};
    background: ${({ theme }) => theme.cardBackground};
    box-shadow: inset 0 0 50px rgba(0, 0, 0, 0.55);
    padding: ${spacing[800]};
    margin-bottom: ${spacing[900]};

    ${media.md} {
        padding: ${spacing[600]};
    }
`;

const Meta = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: ${spacing[300]} ${spacing[700]};
    margin-bottom: ${spacing[600]};
`;

const MetaItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${spacing[100]};

    span:last-child {
        font-size: ${typography.fontSize[200]};
        color: ${({ theme }) => theme.text};
    }
`;

const Lede = styled.p`
    font-size: ${typography.fontSize[400]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.text};
    margin: 0;
    max-width: 62ch;
`;

const Layout = styled.div`
    display: grid;
    grid-template-columns: 280px minmax(0, 1fr);
    gap: ${spacing[900]};
    align-items: start;

    ${media.lg} {
        grid-template-columns: 1fr;
        gap: ${spacing[700]};
    }
`;

const Sidebar = styled.aside`
    position: sticky;
    top: calc(var(--chrome-h, 145px) + ${spacing[600]});
    align-self: start;

    ${media.lg} {
        position: static;
    }
`;

const Body = styled.div`
    min-width: 0;
`;

const Section = styled.section`
    margin-bottom: ${spacing[1000]};
`;

const Para = styled.p`
    font-size: ${typography.fontSize[300]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0 0 ${spacing[500]};
    max-width: 68ch;

    strong {
        color: ${({ theme }) => theme.text};
        font-weight: ${typography.fontWeight.semibold};
    }
`;

const Bullets = styled.ul`
    list-style: none;
    padding: 0;
    margin: 0 0 ${spacing[600]};
    max-width: 68ch;
    columns: 2;
    column-gap: ${spacing[700]};

    ${media.md} {
        columns: 1;
    }
`;

const Bullet = styled.li`
    font-size: ${typography.fontSize[200]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    padding-left: ${spacing[500]};
    position: relative;
    margin-bottom: ${spacing[200]};
    break-inside: avoid;

    &::before {
        content: "\\25B8";
        position: absolute;
        left: 0;
        color: ${({ theme }) => theme.primary};
    }
`;

const Pull = styled.blockquote`
    border: 1px solid ${({ theme }) => theme.primary};
    border-left-width: 2px;
    background: ${({ theme }) => theme.glowSoft};
    padding: ${spacing[600]};
    margin: ${spacing[700]} 0;
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[300]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ theme }) => theme.primary};
    line-height: ${typography.lineHeight[500]};
`;

const DiagramSlot = styled.div`
    margin: ${spacing[700]} 0;
`;

const Shipped = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: ${spacing[500]};
    margin: ${spacing[700]} 0;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
    }
`;

const ShippedItem = styled.div`
    border: 1px solid ${({ theme }) => theme.borderLight};
    border-left: 2px solid ${({ color }) => color};
    padding: ${spacing[500]};
    min-width: 0;
`;

const ShippedName = styled.div`
    font-family: ${typography.fontFamily.display};
    font-size: ${typography.fontSize[200]};
    text-transform: uppercase;
    letter-spacing: ${typography.letterSpacing.wide};
    color: ${({ color }) => color};
    margin-bottom: ${spacing[300]};
`;

const ShippedDesc = styled.p`
    font-size: ${typography.fontSize[150]};
    line-height: ${typography.lineHeight[500]};
    color: ${({ theme }) => theme.subtleText};
    margin: 0;
`;

const WORKFLOWS = [
    {
        name: "Device Onboarding",
        color: colors.primary[500],
        desc: "Discovering a new device, pairing it to the home, naming and placing it, and confirming it responds before the user leaves the flow.",
    },
    {
        name: "Automation",
        color: colors.cyan[300],
        desc: "Rules that tie devices together — triggers, schedules, and scenes — expressed in the app and executed against the hardware.",
    },
    {
        name: "Account Management",
        color: colors.emerald[500],
        desc: "Homes, members, permissions, and the account state every device and automation hangs off.",
    },
    {
        name: "IoT Integrations",
        color: colors.amber[400],
        desc: "Connections to third-party devices and platforms so the home is not limited to a single vendor's hardware.",
    },
];

const ONBOARDING = [
    { name: "Discover", color: colors.primary[500], desc: "The app finds a device that is powered on but not yet claimed." },
    { name: "Pair", color: colors.cyan[300], desc: "The device is bound to the user's home and credentials are exchanged." },
    { name: "Configure", color: colors.emerald[500], desc: "Name, room, and default behavior are set." },
    { name: "Verify", color: colors.amber[400], desc: "A live command confirms the device responds before onboarding is marked complete." },
    { name: "Ready", color: colors.coral[400], desc: "The device appears in the home and is available to automations." },
];

const LIFECYCLE = [
    { name: "Requirements", color: colors.primary[500] },
    { name: "Implementation", color: colors.cyan[300] },
    { name: "Testing", color: colors.emerald[500] },
    { name: "Release", color: colors.amber[400] },
];

const SHIPPED = [
    {
        name: "Native Mobile Features",
        color: colors.primary[500],
        desc: "Production React Native features shipped to both the iOS and Android apps behind tilt's connected-home products.",
    },
    {
        name: "Onboarding & Automation",
        color: colors.cyan[300],
        desc: "The workflows that take a device from the box to a working part of an automated home.",
    },
    {
        name: "Mobile & Web Parity",
        color: colors.emerald[500],
        desc: "Account and device management workflows that behaved consistently across the mobile apps and the web platform.",
    },
    {
        name: "Reusable Patterns",
        color: colors.amber[400],
        desc: "Application patterns and component libraries that kept new device features consistent and faster to build.",
    },
];

const Tilt = () => (
    <PageWrapper>
        <Header>
            <PageHeadline>tilt by Smarterhome</PageHeadline>
            <Meta>
                <MetaItem>
                    <TerminalLabel>Role</TerminalLabel>
                    <span>Software Engineer &mdash; Mobile &amp; IoT</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Service Period</TerminalLabel>
                    <span>Feb 2019 &mdash; Sep 2020</span>
                </MetaItem>
                <MetaItem>
                    <TerminalLabel>Location</TerminalLabel>
                    <span>Provo, Utah &middot; On-site</span>
                </MetaItem>
            </Meta>
            <Lede>
                tilt built connected smart-home products, and the mobile app was how customers
                lived with them. I built production React Native features for the iOS and Android
                apps and the web platform behind them &mdash; device onboarding, automation, account
                management, and IoT integrations. The interesting part was that{" "}
                <strong>every screen had a physical device on the other side of it</strong>, and
                the software had to be honest about what that device was actually doing.
            </Lede>
        </Header>

        <Layout>
            <Sidebar>
                <InfoCard
                    dates="Feb 2019 – Sep 2020"
                    roles={["Software Engineer", "Mobile", "IoT"]}
                    tools={["React Native", "iOS", "Android", "IoT Integrations", "Web"]}
                />
            </Sidebar>

            <Body>
                <Section>
                    <SectionHeadline>The Engineering Problem</SectionHeadline>
                    <Para>
                        Most mobile apps own their state. Tap a button, the app updates, and the
                        screen is the truth. A connected-home app does not get that luxury. The
                        device is the truth, the network between the phone and the device is
                        unreliable, and the user is often standing in front of the hardware
                        watching whether it did what the app said it did.
                    </Para>
                    <ContrastPair
                        left={{
                            label: "Ordinary mobile app",
                            color: colors.coral[400],
                            text: "The app's state is the product. If the screen updated, the action happened.",
                        }}
                        right={{
                            label: "Connected-home app",
                            color: colors.emerald[500],
                            text: "The device's state is the product. The screen is a claim that has to be confirmed by hardware.",
                        }}
                    />
                    <Pull>
                        The app is not the product. It is the control surface for something in
                        someone&rsquo;s house.
                    </Pull>
                    <Para>
                        That reframe shaped how every workflow was built: optimistic where it was
                        safe, confirmed where it mattered, and explicit about the difference when a
                        device did not respond.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>What the App Had to Do</SectionHeadline>
                    <Para>
                        The work spanned four workflow areas, each built for both native platforms
                        and, where it applied, the web platform as well.
                    </Para>
                    <DiagramSlot>
                        <DomainGrid label="Application workflows" domains={WORKFLOWS} />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>Device Onboarding</SectionHeadline>
                    <Para>
                        Onboarding was the workflow with the least room for error. It is the first
                        thing a customer does with a new device, it involves the phone, the device,
                        and the backend agreeing on who owns what, and if it fails the customer is
                        left holding hardware that does nothing.
                    </Para>
                    <DiagramSlot>
                        <PipelineFlow
                            label="Onboarding flow"
                            stages={ONBOARDING}
                            caption="Onboarding is not complete when the form is submitted. It is complete when the device has answered."
                        />
                    </DiagramSlot>
                    <Para>
                        Building it meant handling the states an ordinary form never sees: a device
                        that is discoverable but will not pair, a pairing that succeeds but never
                        verifies, and a user who backs out halfway through and returns with the
                        device in an unknown state.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Automation and Integrations</SectionHeadline>
                    <Para>
                        Once a device was in the home it had to be useful alongside everything else
                        in it. Automation workflows let customers express rules across devices
                        &mdash; a trigger, a schedule, a scene &mdash; and IoT integrations extended
                        those rules to third-party hardware and platforms.
                    </Para>
                    <Para>
                        The engineering constraint was the same as onboarding, at a larger scale:
                        the app was describing behavior that would execute later, against devices
                        it could not see, and it had to present that clearly enough that the
                        customer trusted the rule would fire.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Reusable Patterns</SectionHeadline>
                    <Para>
                        Each new device type wanted its own onboarding quirks, its own controls,
                        and its own place in automations. Building a bespoke screen per device
                        would have made every hardware launch a mobile project. Instead the app
                        was built on reusable patterns and a shared component library:
                    </Para>
                    <Bullets>
                        <Bullet>Device list and detail views</Bullet>
                        <Bullet>Pairing and verification steps</Bullet>
                        <Bullet>Control surfaces per device capability</Bullet>
                        <Bullet>Rule and schedule builders</Bullet>
                        <Bullet>Home and member management</Bullet>
                        <Bullet>Shared state and loading conventions</Bullet>
                    </Bullets>
                    <Para>
                        The payoff was consistency for the customer and velocity for the team: a new
                        device slotted into existing patterns rather than starting from a blank
                        screen.
                    </Para>
                </Section>

                <Section>
                    <SectionHeadline>Across the Lifecycle</SectionHeadline>
                    <Para>
                        I worked on-site with the engineering team across the full product
                        lifecycle, taking requirements and user workflows through implementation,
                        testing, and release on both platforms.
                    </Para>
                    <DiagramSlot>
                        <StepGrid
                            label="Delivery"
                            steps={LIFECYCLE}
                            columns={4}
                            caption="Requirements through release, on iOS and Android, with the web platform kept in step."
                        />
                    </DiagramSlot>
                </Section>

                <Section>
                    <SectionHeadline>What I Shipped</SectionHeadline>
                    <Shipped>
                        {SHIPPED.map((item) => (
                            <ShippedItem key={item.name} color={item.color}>
                                <ShippedName color={item.color}>{item.name}</ShippedName>
                                <ShippedDesc>{item.desc}</ShippedDesc>
                            </ShippedItem>
                        ))}
                    </Shipped>
                    <MetricsCard
                        metrics={[
                            {
                                value: "iOS + Android",
                                label: "Platforms",
                                description: "Native apps built in React Native, plus the web platform",
                                color: colors.emerald[500],
                            },
                            {
                                value: "4 Workflows",
                                label: "Owned",
                                description: "Onboarding, automation, account management, integrations",
                                color: colors.cyan[300],
                            },
                            {
                                value: "Shared Library",
                                label: "Foundation",
                                description: "Reusable patterns and components across device features",
                                color: colors.amber[400],
                            },
                        ]}
                    />
                </Section>

                <Section>
                    <SectionHeadline>Retrospective</SectionHeadline>
                    <Para>
                        tilt is where I learned to distrust the screen. A mobile app that talks to
                        hardware cannot treat its own state as the truth, and building for that
                        &mdash; confirmation over optimism, explicit failure over silent success
                        &mdash; carried straight into the NFC hardware and recovery workflows I
                        later built for Nebo.
                    </Para>
                    <Pull>
                        When there is a physical device on the other side of the screen, the
                        software has to earn the right to say &ldquo;done.&rdquo;
                    </Pull>
                </Section>
            </Body>
        </Layout>

        <NDADisclosure />
    </PageWrapper>
);

export default Tilt;
